import { Button, DialogActions, DialogContent, IconButton } from "@mui/material";
import { CreateBase, DateInput, ReferenceInput, required, SelectInput, useGetOne } from "react-admin";
import { Column } from "../../misc/Column";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { Form2 } from "../../misc/Form2";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { Row } from "../../misc/Row";
import AddIcon from "@mui/icons-material/Add";
import { date, dateMaxYear } from "../../utils/validators";
import { useFormContext, useWatch } from "react-hook-form";
import { DateTime, WeekdayNumbers } from "luxon";
import { useCallback, useEffect, useState } from "react";
import _sum from "lodash/sum";
import { weekdayToWeekdayNumber } from "../../model/Weekday";
import { type CourseTemplate } from "../../providers/courseTemplatesProvider";
import { type DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { type Timetable } from "../../providers/timetablesProvider";
import { BooleanInput } from "../../inputs/BooleanInput";
import { LecturerInput } from "../../inputs/LecturerInput";
import { RoomInput } from "../../inputs/RoomInput";
import { SaveButton2 } from "../../buttons/SaveButton2";

interface CreateCourseFormProps {
  initialCourseTemplate: CourseTemplate;
  goBack: () => void;
  onSuccess: () => void;
}

interface CreateCourseFormValues {
  drivingSchoolId: string;
  courseTemplateId: string;
  branchId?: string;
  startDate?: string;
  endDate?: string;
  timetableId?: string;
}

export function CreateBKFCourseForm(props: CreateCourseFormProps) {
  const { initialCourseTemplate, goBack, onSuccess } = props;
  const [{ drivingSchoolId }] = useAutovioContext();

  return (
    <CreateBase
      resource="courses"
      record={{
        drivingSchoolId,
        courseTemplateId: initialCourseTemplate.id,
      }}
      mutationOptions={{ onSuccess, onError: (error) => handleError("Fehler beim Erstellen des Kurses.", error) }}
    >
      <Form2>
        <DialogContent>
          <Column sx={{ alignItems: "stretch" }}>
            <Row gap={1} sx={{ alignItems: "center" }}>
              <ReferenceInput
                source="courseTemplateId"
                reference="courseTemplates"
                filter={{ drivingSchoolId }}
                sort={{ field: "name", order: "ASC" }}
                perPage={999}
              >
                <SelectInput label="Kursvorlage" validate={required("Bitte wähle eine Kursvorlage aus.")} fullWidth />
              </ReferenceInput>
              <IconButton onClick={() => window.open("/courseTemplates/create", "_blank")}>
                <AddIcon />
              </IconButton>
            </Row>
            <Row gap={1} sx={{ alignItems: "center" }}>
              <ReferenceInput
                source="branchId"
                reference="branches"
                filter={{ drivingSchoolId }}
                sort={{ field: "name", order: "ASC" }}
                perPage={999}
              >
                <SelectInput
                  label="Filiale"
                  validate={required("Bitte wähle die Filiale aus, wo der Kurs stattfinden soll.")}
                  fullWidth
                />
              </ReferenceInput>
              <IconButton onClick={() => window.open("/branches/create", "_blank")}>
                <AddIcon />
              </IconButton>
            </Row>
            <Row gap={1} sx={{ alignItems: "center" }}>
              <ReferenceInput
                source="timetableId"
                reference="timetables"
                filter={{ drivingSchoolId }}
                sort={{ field: "name", order: "ASC" }}
                perPage={999}
              >
                <SelectInput label="Studenplan" validate={required("Bitte wähle einen Stundenplan aus.")} fullWidth />
              </ReferenceInput>
              <IconButton onClick={() => window.open("/timetables/create", "_blank")}>
                <AddIcon />
              </IconButton>
            </Row>
            <Row gap={1} sx={{ alignItems: "center" }}>
              <DateInput
                label="Kursbeginn"
                source="startDate"
                validate={[
                  required("Bitte wähle das Datum aus, an dem der Kurs starten soll."),
                  date(),
                  dateMaxYear(2099),
                ]}
                fullWidth
              />
              <div style={{ width: "43px" }} />
            </Row>
            <Row gap={1} sx={{ alignItems: "center" }}>
              <_EndDateInput />
              <div style={{ width: "43px" }} />
            </Row>
            <_AutoCreateLessonsInputs />
          </Column>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={goBack}>
            Zurück
          </Button>
          <SaveButton2 label="Kurs erstellen" />
        </DialogActions>
      </Form2>
    </CreateBase>
  );
}

function _EndDateInput() {
  const { drivingSchoolId, courseTemplateId, branchId, timetableId, startDate, endDate } =
    useWatch<CreateCourseFormValues>();
  const { setValue, setError, clearErrors } = useFormContext();
  const [endDateTouched, setEndDateTouched] = useState(false);
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: drivingSchoolId! },
    { enabled: !!drivingSchoolId },
  );
  const branch = drivingSchool?.branches?.find((it) => it.id === branchId);
  const { data: courseTemplate } = useGetOne<CourseTemplate>(
    "courseTemplates",
    { id: courseTemplateId! },
    { enabled: !!courseTemplateId },
  );
  const { data: timetable } = useGetOne<Timetable>("timetables", { id: timetableId! }, { enabled: !!timetableId });
  const [minEndDate, setMinEndDate] = useState<DateTime | undefined>();
  const notEnoughTimeMessage = (minEndDate: DateTime) => {
    return `Das Kursende kann nicht vor dem ${minEndDate.toFormat("dd.MM.yyyy")} liegen.`;
  };
  const enoughTime = useCallback(
    (endDate: string) => {
      const endDate_ = DateTime.fromISO(endDate ?? "");
      if (minEndDate && endDate_ < minEndDate) {
        return notEnoughTimeMessage(minEndDate);
      }
    },
    [minEndDate],
  );
  useEffect(() => {
    const startDate_ = DateTime.fromISO(startDate ?? "");
    if (startDate_.isValid && courseTemplate && branch && timetable) {
      const ueByWeekdayNumber: Record<WeekdayNumbers, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
      for (const [weekday, _from, _to, ue] of timetable.lessonsSchedule) {
        const weekdayNumber = weekdayToWeekdayNumber(weekday);
        ueByWeekdayNumber[weekdayNumber] += ue;
      }
      const publicHolidays = new Set<number>();
      for (const { date } of branch.publicHolidays ?? []) {
        publicHolidays.add(date);
      }
      let remainingUE = _sum(courseTemplate?.modules.map((it) => it.ue));
      let d = startDate_;
      // eslint-disable-next-line no-constant-condition
      for (let i = 0; true; ++i) {
        if (!publicHolidays.has(d.year * 10000 + d.month * 100 + d.day)) {
          const ue = ueByWeekdayNumber[d.weekday];
          if (ue > 0) {
            remainingUE -= ue;
            if (remainingUE <= 0) {
              break;
            }
          }
        }
        if (i >= 1000) {
          console.error("Could not determine minEndDate after iterating over 1000 days");
          return;
        }
        d = d.plus({ days: 1 });
      }
      if (!endDateTouched) {
        setValue("endDate", d.toISODate());
      } else if (endDate) {
        const endDate_ = DateTime.fromISO(endDate);
        if (endDate_.isValid && endDate_ < d) {
          setError("endDate", { message: notEnoughTimeMessage(d) });
        } else {
          clearErrors("endDate");
        }
      }
      setMinEndDate(d);
    }
  }, [courseTemplate, branch, timetable, startDate, endDate, endDateTouched]);

  return (
    <DateInput
      label="Kursende"
      source="endDate"
      onChange={() => setEndDateTouched(true)}
      validate={[required("Bitte wähle das Datum aus, an dem der Kurs enden soll."), date(), enoughTime]}
      fullWidth
    />
  );
}

function _AutoCreateLessonsInputs() {
  const autoCreateLessons = useWatch<{ autoCreateLessons?: boolean }>({ name: "autoCreateLessons" });
  const branchId = useWatch<{ branchId?: string }>({ name: "branchId" });
  return (
    <>
      <BooleanInput
        variant="checkbox"
        source="autoCreateLessons"
        label="Kurs automatisch mit Terminen füllen (Beta)"
        size="small"
      />
      {autoCreateLessons && (
        <Row gap={1} sx={{ alignItems: "center" }}>
          <LecturerInput source="autoCreateLessonsLecturerId" size="small" helperText={"\u00A0"} fullWidth />
          <div style={{ width: "43px" }} />
        </Row>
      )}
      {autoCreateLessons && (
        <Row gap={1} sx={{ alignItems: "center" }}>
          <RoomInput
            source="autoCreateLessonsRoomId"
            branchId={branchId}
            size="small"
            helperText={"\u00A0"}
            fullWidth
          />
          <IconButton onClick={() => window.open("/rooms/create", "_blank")}>
            <AddIcon />
          </IconButton>
        </Row>
      )}
    </>
  );
}
